import { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode, RefObject } from 'react';
import type { ApiClient } from '../api/client.js';
import { createTrackingBuffer } from '../lib/trackingBuffer.js';
import { useAnalytics } from '../hooks/useAnalytics.js';
import { usePageViewTracking } from '../hooks/usePageViewTracking.js';
import { useSessionEvents } from '../hooks/useSessionEvents.js';

interface TrackModelClickInput {
  modelId: string;
  modelName: string;
  provider?: string | null;
  section?: string | null;
  viewDurationMs?: number | null;
}

export interface AnalyticsValue {
  sessionId: string | null;
  trackSectionClick: (section: string) => void;
  trackSearch: () => void;
  trackFavoriteAdded: () => void;
  trackModelView: (count?: number) => void;
  trackModelNameCopied: () => void;
  trackModelClick: (input: TrackModelClickInput) => void;
  trackAiRequest: () => void;
}

const noop = () => undefined;
const AnalyticsContext = createContext<AnalyticsValue>({
  sessionId: null,
  trackSectionClick: noop,
  trackSearch: noop,
  trackFavoriteAdded: noop,
  trackModelView: noop,
  trackModelNameCopied: noop,
  trackModelClick: noop,
  trackAiRequest: noop,
});

export function AnalyticsProvider({
  api,
  userId,
  currentPath,
  containerRef,
  children,
}: {
  api: ApiClient;
  userId?: string;
  currentPath: string;
  containerRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const buffer = useMemo(() => createTrackingBuffer(api), [api]);
  const { sessionId } = useAnalytics(userId, api, buffer);
  const tracker = useSessionEvents(userId, sessionId, buffer, containerRef);

  usePageViewTracking(userId, sessionId, currentPath, buffer, tracker.recordNavigationDuration);

  useEffect(() => () => buffer.destroy(), [buffer]);

  const value = useMemo<AnalyticsValue>(
    () => ({
      sessionId,
      trackSectionClick(section) {
        if (!userId) return;
        buffer.enqueue({ type: 'track-section-click', payload: { section } });
      },
      trackSearch: tracker.trackSearch,
      trackFavoriteAdded: tracker.trackFavoriteAdded,
      trackModelView: tracker.trackModelView,
      trackModelNameCopied: tracker.trackModelNameCopied,
      trackModelClick(input) {
        if (!userId || !sessionId) return;
        tracker.trackModelCardClick();
        buffer.enqueue({
          type: 'track-model-click',
          payload: {
            sessionId,
            modelId: input.modelId,
            modelName: input.modelName,
            provider: input.provider ?? null,
            section: input.section ?? null,
            viewDurationMs: input.viewDurationMs ?? null,
          },
        });
      },
      trackAiRequest: tracker.trackAiRequest,
    }),
    [buffer, sessionId, tracker, userId],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export function useAnalyticsTracking(): AnalyticsValue {
  return useContext(AnalyticsContext);
}
